import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "step",
    "progressBar",
    "matchScore",
    "matchScoreHighlight",
    "nationalityContainer",
  ];

  connect() {
    this.currentStep = 1;
    this.totalSteps = 4;
  }

  open() {
    const modal = document.getElementById("autobot-onboarding-modal");
    modal.classList.remove("hidden");
    modal.classList.add("flex");
    document.body.classList.add("overflow-hidden");
    this.currentStep = 1;
    this.showCurrentStep();
  }

  close() {
    this.element.classList.remove("flex");
    this.element.classList.add("hidden");
    document.body.classList.remove("overflow-hidden");
  }

  nextStep() {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.showCurrentStep();
    } else {
      this.finish();
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.showCurrentStep();
    }
  }

  showCurrentStep() {
    this.stepTargets.forEach((el, i) => {
      if (i + 1 === this.currentStep) {
        el.classList.remove("hidden");
        el.classList.add("flex");
      } else {
        el.classList.add("hidden");
        el.classList.remove("flex");
      }
    });
  }

  updateMatchScore(event) {
    const value = Number(event.target.value);
    let label = "Flexible (0%+)";
    if (value >= 85) label = "Highest (85%+)";
    else if (value >= 70) label = "Higher (70%+)";
    else if (value >= 50) label = "High (50%+)";

    if (this.hasMatchScoreHighlightTarget) {
      this.matchScoreHighlightTarget.textContent = `${value}% - ${label}`;
    }
    if (this.hasMatchScoreTarget) {
      this.matchScoreTarget.style.width = `${value}%`;
    }
    this.saveField(event.target.dataset.field, value);
  }

  updateTextField(event) {
    const field = event.target.dataset.field;
    const value = event.target.value;
    this.saveField(field, value);
  }

  selectSearchMethod(event) {
    const clickedCard = event.currentTarget;
    const value = clickedCard.dataset.value;
    const meta = {
      hunter: {
        icon: "bolt",
        title: "Relentless Hunter",
        desc: "Apply to everything matching criteria immediately. High volume, high speed.",
      },
      scout: {
        icon: "ads_click",
        title: "Curated Scout",
        desc: "Send me a daily digest of top matches. Focus on quality over quantity.",
      },
    };

    const container = clickedCard.parentElement;
    const cards = container.querySelectorAll(
      '[data-action="click->autobot-config#selectSearchMethod"]',
    );

    cards.forEach((card) => {
      const cardValue = card.dataset.value;
      const m = meta[cardValue] || meta.hunter;
      const isSelected = card === clickedCard;
      card.dataset.selected = isSelected ? "true" : "false";
      card.className = `autobot-option-card${isSelected ? " autobot-option-card--selected" : ""}`;
      card.innerHTML = this._optionCardInner(m);
    });

    this.saveField("search_method", value);
  }

  selectCoverLetterStrategy(event) {
    const clickedCard = event.currentTarget;
    const value = clickedCard.dataset.value;
    const meta = {
      ai: {
        icon: "smart_toy",
        title: "Auto-Generate with AI",
        desc: "Tailored letter per application.",
      },
      manual: {
        icon: "upload_file",
        title: "Send Resume Only",
        desc: "Focus on rapid application volume.",
      },
    };

    const container = clickedCard.parentElement;
    const cards = container.querySelectorAll(
      '[data-action="click->autobot-config#selectCoverLetterStrategy"]',
    );

    cards.forEach((card) => {
      const cardValue = card.dataset.value;
      const m = meta[cardValue] || meta.ai;
      const isSelected = card === clickedCard;
      card.dataset.selected = isSelected ? "true" : "false";
      card.className = `autobot-option-card${isSelected ? " autobot-option-card--selected" : ""}`;
      card.innerHTML = this._optionCardInner(m);
    });

    this.saveField("cover_letter_strategy", value);
  }

  selectVisaOption(event) {
    const clickedCard = event.currentTarget;
    const value = clickedCard.dataset.value;

    const container = clickedCard.parentElement;
    const cards = container.querySelectorAll(
      '[data-action="click->autobot-config#selectVisaOption"]',
    );

    cards.forEach((card) => {
      const isSelected = card === clickedCard;
      card.dataset.selected = isSelected ? "true" : "false";
      card.className = "autobot-pill autobot-pill--wide";
    });

    this.saveField("visa_sponsorship", value);
  }

  selectAutomationEngine(event) {
    const clickedCard = event.currentTarget;
    const value = clickedCard.dataset.value;

    const meta = {
      cloakbrowser: {
        icon: "shield",
        title: "CloakBrowser Stealth",
        desc: "Local stealth Chromium. Free, no usage limits, source-level anti-detection.",
      },
      legacy: {
        icon: "ads_click",
        title: "Legacy (JobSpy)",
        desc: "Playwright automation + AI autofill.",
      },
      auto: {
        icon: "shuffle",
        title: "Auto (Smart)",
        desc: "Smart fallback chain: CloakBrowser → Local Playwright → agent-browser.",
      },
    };

    const container = clickedCard.parentElement;
    const cards = container.querySelectorAll(
      '[data-action="click->autobot-config#selectAutomationEngine"]',
    );

    cards.forEach((card) => {
      const cardValue = card.dataset.value;
      const m = meta[cardValue] || meta.cloakbrowser;

      const isSelected = card === clickedCard;
      card.dataset.selected = isSelected ? "true" : "false";
      card.className = `autobot-option-card${isSelected ? " autobot-option-card--selected" : ""}`;
      card.innerHTML = this._optionCardInner(m);
    });

    this.saveField("automation_engine", value);
  }

  selectSubmissionMode(event) {
    const clickedCard = event.currentTarget;
    const value = clickedCard.dataset.value;

    const meta = {
      auto: {
        icon: "send",
        title: "Auto-submit",
        desc: "Copilot completes and submits the application.",
      },
      review: {
        icon: "visibility",
        title: "Review before submit",
        desc: "Fill everything and stop before final submit.",
      },
    };

    const container = clickedCard.parentElement;
    const cards = container.querySelectorAll(
      '[data-action="click->autobot-config#selectSubmissionMode"]',
    );

    cards.forEach((card) => {
      const cardValue = card.dataset.value;
      const m = meta[cardValue] || meta.auto;

      const isSelected = card === clickedCard;
      card.dataset.selected = isSelected ? "true" : "false";
      card.className = `autobot-option-card${isSelected ? " autobot-option-card--selected" : ""}`;
      card.innerHTML = this._optionCardInner(m);
    });

    this.saveField("submission_mode", value);
  }

  // Shared option-card body. MUST stay byte-identical to the option-card
  // inner markup in _autobot_onboarding_modal_v2.html.erb (the Veridian
  // radio-dot indicator, Material Symbols icon, title, desc) so a click that
  // regenerates a card keeps the exact same look as the server-rendered one.
  _optionCardInner(m) {
    return `
                <div class="autobot-option-card__indicator">
                    <span class="material-symbols-outlined" style="font-size:14px" aria-hidden="true">check</span>
                </div>
                <span class="material-symbols-outlined autobot-option-card__icon" aria-hidden="true">${m.icon}</span>
                <p class="autobot-option-card__title">${m.title}</p>
                <p class="autobot-option-card__desc">${m.desc}</p>
            `;
  }

  addNationality(event) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    this._addNationalityLogic(event.currentTarget);
  }

  addNationalityClick(event) {
    const container = event.currentTarget.closest(".autobot-chip-input-field");
    const input = container.querySelector("input");
    this._addNationalityLogic(input);
  }

  _addNationalityLogic(input) {
    const value = input.value.trim();
    if (value) {
      const container = this.nationalityContainerTarget;
      if (!container) return;

      const chip = document.createElement("div");
      chip.className = "nationality-chip autobot-chip autobot-chip--accent";
      chip.dataset.value = value;
      chip.innerHTML = `${value}<button type="button" class="autobot-chip__remove" aria-label="Remove ${value}" data-action="click->autobot-config#removeNationalityChip"><span class="material-symbols-outlined" style="font-size:14px" aria-hidden="true">close</span></button>`;

      const wrapper = input.closest(".autobot-chip-input-field");
      container.insertBefore(chip, wrapper);
      input.value = "";

      this.syncNationalities();
    }
  }

  syncNationalities() {
    const container = this.nationalityContainerTarget;
    const tags = container.querySelectorAll(".nationality-chip");
    const values = Array.from(tags).map((t) => t.dataset.value);
    this.saveField("nationality", values);
  }

  removeNationalityChip(event) {
    const chip = event.currentTarget.closest(".nationality-chip");
    if (chip) {
      chip.remove();
      this.syncNationalities();
    }
  }

  toggleSelection(event) {
    const btn = event.currentTarget;
    const isActive = btn.dataset.active === "true";

    if (btn.dataset.group) {
      const groupBtns = this.element.querySelectorAll(
        `[data-group="${btn.dataset.group}"]`,
      );
      groupBtns.forEach((b) => {
        b.dataset.active = "false";
      });
      btn.dataset.active = "true";
    } else {
      btn.dataset.active = (!isActive).toString();
    }
    this.syncData(btn);
  }

  syncData(btn) {
    const field = btn.dataset.field;
    if (!field) return;
    let value;
    if (btn.dataset.group) {
      value = btn.dataset.value;
    } else {
      const activeBtns = this.element.querySelectorAll(
        `[data-field="${field}"][data-active="true"]`,
      );
      value = Array.from(activeBtns).map((b) => b.dataset.value);
    }
    this.saveField(field, value);
  }

  addKeyword(event) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    this._addKeywordLogic(event.target);
  }

  addKeywordClick(event) {
    // Find the input in the same container
    const container =
      event.currentTarget.closest(".autobot-chip-input-field") ||
      event.currentTarget.closest("[data-keyword-container]");
    const input = container.querySelector("input");

    if (input) {
      this._addKeywordLogic(input);
    }
  }

  _addKeywordLogic(input) {
    const value = input.value.trim();
    if (!value) return;

    const field = input.dataset.field;
    const container = this.element.querySelector(
      `[data-keyword-container="${field}"]`,
    );
    if (!container) return;

    // Don't duplicate if already exists
    const exists = Array.from(
      container.querySelectorAll("div[data-value]"),
    ).some((t) => t.dataset.value === value);
    if (exists) {
      input.value = "";
      return;
    }

    const tag = document.createElement("div");
    tag.className = "autobot-chip";
    tag.dataset.value = value;
    tag.innerHTML = `${value}<button type="button" class="autobot-chip__remove" aria-label="Remove ${value}" data-action="click->autobot-config#removeKeyword"><span class="material-symbols-outlined" style="font-size:14px" aria-hidden="true">close</span></button>`;

    // Insert before the input container wrapper if it exists, or just before input
    const inputWrapper = input.closest(".autobot-chip-input-field");
    if (inputWrapper) {
      container.insertBefore(tag, inputWrapper);
    } else {
      container.insertBefore(tag, input);
    }

    input.value = "";
    this.syncKeywords(field);
  }

  removeKeyword(event) {
    const tag = event.currentTarget.closest("div[data-value]");
    if (!tag) return;
    const container = tag.closest("[data-keyword-container]");
    const field = container.dataset.keywordContainer;
    tag.remove();
    this.syncKeywords(field);
  }

  syncKeywords(field) {
    const container = this.element.querySelector(
      `[data-keyword-container="${field}"]`,
    );
    const values = Array.from(
      container.querySelectorAll("div[data-value]"),
    ).map((t) => t.dataset.value);
    this.saveField(field, values);
  }

  saveField(field, value) {
    const formData = new FormData();
    formData.append(
      `user[autobot_settings][${field}]`,
      Array.isArray(value) ? value.join(",") : value,
    );

    fetch("/users", {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          .content,
        Accept: "application/json",
      },
      body: formData,
    });
  }

  finish() {
    this.close();
    window.location.reload();
  }

  selectOnEnter(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.currentTarget.click();
    }
  }
}
